import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Badge, Button, Alert, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { formatFechaPartido } from '../../utils/fechaUtils';

const CoachPartidoDetalles = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [partido, setPartido] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enviandoNotificacion, setEnviandoNotificacion] = useState(false);
  const [tieneLogros, setTieneLogros] = useState(false);

  const isMobile = window.innerWidth <= 768;

  // Verificar si el partido tiene logros asignados
  const verificarLogrosPartido = async () => {
    try {
      const token = localStorage.getItem('prokids_token');
      const response = await fetch(`/api/coach/partidos/${id}/logros`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('🔍 Resultado de verificarLogrosPartido:', result);
        if (result.success && result.logros) {
          // Verificar si hay al menos un logro
          const hayLogros = Object.keys(result.logros).length > 0;
          console.log('✅ Hay logros:', hayLogros, 'Total:', Object.keys(result.logros).length);
          setTieneLogros(hayLogros);
        } else {
          console.log('❌ No hay logros o success es false');
          setTieneLogros(false);
        }
      } else {
        console.log('❌ Response no OK:', response.status);
      }
    } catch (error) {
      console.error('Error al verificar logros:', error);
    }
  };

  // Cargar detalles del partido
  useEffect(() => {
    const fetchPartidoDetalles = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('prokids_token');

        const response = await fetch(`/api/coach/partidos/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setPartido(result.data);
            console.log('🏟️ Partido cargado:', result.data.id, 'Estado:', result.data.estado);

            // Si el partido está finalizado, verificar si tiene logros
            if (result.data.estado === 'finalizado') {
              console.log('✅ Partido finalizado, verificando logros...');
              verificarLogrosPartido();
            } else {
              console.log('⏳ Partido no finalizado, estado:', result.data.estado);
            }
          } else {
            setError('Error al cargar detalles del partido');
          }
        } else {
          setError(`Error del servidor: ${response.status}`);
        }
      } catch (error) {
        setError('Error de conexión al cargar detalles del partido');
        console.error('Error al cargar detalles del partido:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPartidoDetalles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Notificar entrega de ProPoints
  const handleNotificarProPoints = async () => {
    try {
      setEnviandoNotificacion(true);
      const token = localStorage.getItem('prokids_token');

      const response = await fetch(`/api/coach/partidos/${id}/notificar-propoints`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast.success('✅ Notificaciones enviadas exitosamente a tutores y futbolistas');

        // Mostrar detalles del resultado
        const { notificaciones_inapp, emails } = result.data;

        if (notificaciones_inapp?.data) {
          toast.info(`📱 ${notificaciones_inapp.data.successCount} notificaciones in-app enviadas`);
        }

        if (emails?.data) {
          toast.info(`📧 ${emails.data.successCount} emails enviados`);
        }
      } else {
        toast.error(result.message || 'Error al enviar notificaciones');
      }
    } catch (error) {
      console.error('Error al notificar ProPoints:', error);
      toast.error('Error de conexión al enviar notificaciones');
    } finally {
      setEnviandoNotificacion(false);
    }
  };

  // Función para formatear hora
  const formatearHora = (hora) => {
    if (!hora) return 'Hora no disponible';
    return hora.substring(0, 5); // HH:MM
  };

  // Función para obtener color del estado
  const getEstadoColor = (estado) => {
    const colores = {
      'preorganizado': 'warning',
      'confirmado': 'success',
      'finalizado': 'secondary',
      'suspendido': 'danger'
    };
    return colores[estado] || 'secondary';
  };

  // Función para obtener texto de posición
  const getPosicionTexto = (posicion) => {
    const posiciones = {
      1: 'Portero',
      2: 'Defensa',
      3: 'Centrocampista',
      4: 'Delantero'
    };
    return posiciones[posicion] || 'Sin posición';
  };

  // Función para obtener color de fondo según posición
  const getPosicionColor = (posicion, esMiEquipo = true) => {
    const coloresMiEquipo = {
      1: '#e8f5e8', // Portero - Verde claro
      2: '#e3f2fd', // Defensa - Azul claro
      3: '#fff3e0', // Centrocampista - Naranja claro
      4: '#fce4ec'  // Delantero - Rosa claro
    };

    const coloresRival = {
      1: '#f1f8e9', // Portero - Verde muy claro
      2: '#e1f5fe', // Defensa - Azul muy claro
      3: '#fff8e1', // Centrocampista - Naranja muy claro
      4: '#fdf2f8'  // Delantero - Rosa muy claro
    };

    return esMiEquipo ? (coloresMiEquipo[posicion] || '#f8f9fa') : (coloresRival[posicion] || '#f8f9fa');
  };

  // Función para obtener color de borde según posición
  const getPosicionBorderColor = (posicion) => {
    const colores = {
      1: '#4caf50', // Portero - Verde
      2: '#2196f3', // Defensa - Azul
      3: '#ff9800', // Centrocampista - Naranja
      4: '#e91e63'  // Delantero - Rosa
    };
    return colores[posicion] || '#e1e5e9';
  };

  // Función para ordenar jugadores por posición
  const ordenarJugadoresPorPosicion = (jugadores) => {
    return [...jugadores].sort((a, b) => {
      // Primero por posición (1=Portero, 2=Defensa, 3=Centro, 4=Delantero)
      if (a.posicionAsignada !== b.posicionAsignada) {
        return (a.posicionAsignada || 5) - (b.posicionAsignada || 5);
      }
      // Luego por estado titular (titulares primero)
      if (a.esTitular !== b.esTitular) {
        if (a.esTitular === true) return -1;
        if (b.esTitular === true) return 1;
      }
      // Finalmente por nombre
      return a.nombre.localeCompare(b.nombre);
    });
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh'
      }}>
        <div style={{ textAlign: 'center', color: '#4352A9' }}>
          <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⚽</div>
          <p>Cargando detalles del partido...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: isMobile ? '40px 20px' : '60px 40px',
        textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e1e5e9'
      }}>
        <div style={{ fontSize: '2rem', marginBottom: '16px' }}>❌</div>
        <h3 style={{
          fontSize: isMobile ? '1.2rem' : '1.4rem',
          fontWeight: '600',
          color: '#dc3545',
          margin: '0 0 8px 0'
        }}>
          Error al cargar detalles del partido
        </h3>
        <p style={{
          fontSize: isMobile ? '14px' : '15px',
          color: '#6c757d',
          margin: '0 0 24px 0'
        }}>
          {error}
        </p>
        <Button
          variant="primary"
          onClick={() => navigate('/coach/partidos')}
          style={{
            backgroundColor: '#4352A9',
            borderColor: '#4352A9'
          }}
        >
          Volver a Mis Partidos
        </Button>
      </div>
    );
  }

  if (!partido) {
    return (
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: isMobile ? '40px 20px' : '60px 40px',
        textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e1e5e9'
      }}>
        <div style={{ fontSize: '2rem', marginBottom: '16px' }}>🔍</div>
        <h3 style={{
          fontSize: isMobile ? '1.2rem' : '1.4rem',
          fontWeight: '600',
          color: '#6c757d',
          margin: '0 0 8px 0'
        }}>
          Partido no encontrado
        </h3>
        <Button
          variant="primary"
          onClick={() => navigate('/coach/partidos')}
          style={{
            backgroundColor: '#4352A9',
            borderColor: '#4352A9'
          }}
        >
          Volver a Mis Partidos
        </Button>
      </div>
    );
  }

  return (
    <div style={{
      padding: isMobile ? '20px' : '24px',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: isMobile ? '20px' : '24px'
      }}>
        <div>
          <h1 style={{
            fontSize: isMobile ? '1.5rem' : '2rem',
            fontWeight: '700',
            color: '#061156',
            margin: 0
          }}>
            ⚽ Detalles del Partido #{partido.id}
          </h1>
          <p style={{
            fontSize: isMobile ? '14px' : '16px',
            color: '#6c757d',
            margin: '4px 0 0 0'
          }}>
            {partido.modalidadTexto} - {formatFechaPartido(partido.fecha, partido.jornada.fecha)}
          </p>
        </div>
        <Button
          variant="outline-primary"
          onClick={() => navigate('/coach/partidos')}
          style={{
            borderColor: '#4352A9',
            color: '#4352A9'
          }}
        >
          ← Volver
        </Button>
      </div>

      {/* Información del partido */}
      <Card style={{
        marginBottom: isMobile ? '20px' : '24px',
        border: '1px solid #e1e5e9',
        borderRadius: '12px'
      }}>
        <Card.Body style={{ padding: isMobile ? '20px' : '24px' }}>
          <Row>
            <Col md={6}>
              <h5 style={{ color: '#061156', marginBottom: '16px' }}>📅 Información del Partido</h5>
              <div style={{ marginBottom: '12px' }}>
                <strong>Fecha:</strong> {formatFechaPartido(partido.fecha, partido.jornada.fecha)}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong>Hora:</strong> {formatearHora(partido.hora || partido.jornada.hora)}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong>Estado:</strong>{' '}
                <Badge bg={getEstadoColor(partido.estado)}>
                  {partido.estadoTexto}
                </Badge>
              </div>
            </Col>
            <Col md={6}>
              <h5 style={{ color: '#061156', marginBottom: '16px' }}>📍 Sede</h5>
              <div style={{ marginBottom: '12px' }}>
                <strong>Nombre:</strong> {partido.sede.nombre}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong>Dirección:</strong> {partido.sede.direccion}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong>Población:</strong> {partido.sede.poblacion}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Anotaciones del coordinador */}
      {partido.anotaciones && (partido.anotaciones.generales?.length > 0 || partido.anotaciones.miEquipo?.length > 0) && (
        <Card style={{
          borderRadius: '12px',
          marginBottom: isMobile ? '20px' : '24px',
          border: '2px solid #ffc107',
          boxShadow: '0 2px 8px rgba(255, 193, 7, 0.15)'
        }}>
          <Card.Header style={{
            backgroundColor: '#fff8e1',
            borderBottom: '2px solid #ffc107',
            fontWeight: '700',
            fontSize: isMobile ? '16px' : '18px',
            color: '#061156'
          }}>
            📋 Notas del coordinador
          </Card.Header>
          <Card.Body style={{ padding: isMobile ? '16px' : '20px' }}>
            {/* Anotaciones generales */}
            {partido.anotaciones.generales?.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h6 style={{ color: '#6c757d', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                  🔵 GENERAL
                </h6>
                {partido.anotaciones.generales.map(anot => (
                  <div
                    key={anot.id}
                    style={{
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '8px',
                      borderLeft: `4px solid ${anot.tipo === 'instruccion' ? '#0d6efd' : anot.tipo === 'cambio' ? '#fd7e14' : '#6c757d'}`
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ marginRight: '6px' }}>
                        {anot.tipo === 'instruccion' ? '📌' : anot.tipo === 'cambio' ? '🔄' : '👁'}
                      </span>
                      <strong style={{ fontSize: '12px', color: anot.tipo === 'instruccion' ? '#0d6efd' : anot.tipo === 'cambio' ? '#fd7e14' : '#6c757d' }}>
                        {anot.tipo === 'instruccion' ? 'Instrucción' : anot.tipo === 'cambio' ? 'Cambio' : 'Observación'}
                      </strong>
                      <small style={{ color: '#adb5bd', marginLeft: '8px', fontSize: '11px' }}>
                        {new Date(anot.created_at).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </small>
                    </div>
                    <p style={{ margin: 0, fontSize: '14px', color: '#333' }}>{anot.contenido}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Anotaciones de mi equipo */}
            {partido.anotaciones.miEquipo?.length > 0 && (
              <div>
                <h6 style={{ color: '#198754', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                  🟢 TU EQUIPO
                </h6>
                {partido.anotaciones.miEquipo.map(anot => (
                  <div
                    key={anot.id}
                    style={{
                      backgroundColor: '#f0fdf4',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '8px',
                      borderLeft: `4px solid ${anot.tipo === 'instruccion' ? '#0d6efd' : anot.tipo === 'cambio' ? '#fd7e14' : '#6c757d'}`
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ marginRight: '6px' }}>
                        {anot.tipo === 'instruccion' ? '📌' : anot.tipo === 'cambio' ? '🔄' : '👁'}
                      </span>
                      <strong style={{ fontSize: '12px', color: anot.tipo === 'instruccion' ? '#0d6efd' : anot.tipo === 'cambio' ? '#fd7e14' : '#6c757d' }}>
                        {anot.tipo === 'instruccion' ? 'Instrucción' : anot.tipo === 'cambio' ? 'Cambio' : 'Observación'}
                      </strong>
                      <small style={{ color: '#adb5bd', marginLeft: '8px', fontSize: '11px' }}>
                        {new Date(anot.created_at).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </small>
                    </div>
                    <p style={{ margin: 0, fontSize: '14px', color: '#333' }}>{anot.contenido}</p>
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Equipos y Jugadores */}
      <Row>
        {/* Mi Equipo */}
        {partido.miEquipo && (
          <Col lg={6} style={{ marginBottom: isMobile ? '20px' : '24px' }}>
            <Card style={{
              border: '2px solid #4352A9',
              borderRadius: '12px'
            }}>
              <Card.Header style={{
                backgroundColor: '#e3f2fd',
                color: '#1a1a1a',
                fontWeight: '700',
                fontSize: isMobile ? '16px' : '18px',
                border: '2px solid #4352A9',
                borderBottom: '4px solid #4352A9'
              }}>
                🏃‍♂️ Mi Equipo: <span style={{ color: '#4352A9', fontWeight: '800' }}>{partido.miEquipo.nombre}</span>
              </Card.Header>
              <Card.Body style={{ padding: isMobile ? '16px' : '20px' }}>
                {partido.miEquipo.jugadores.length > 0 ? (
                  <div>
                    {ordenarJugadoresPorPosicion(partido.miEquipo.jugadores).map((jugador, _index) => (
                      <div
                        key={jugador.id}
                        style={{
                          padding: '12px',
                          marginBottom: '8px',
                          backgroundColor: getPosicionColor(jugador.posicionAsignada, true),
                          borderRadius: '8px',
                          border: `2px solid ${getPosicionBorderColor(jugador.posicionAsignada)}`,
                          borderLeft: `6px solid ${getPosicionBorderColor(jugador.posicionAsignada)}`
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div>
                            <strong style={{ color: '#1a1a1a', fontSize: '15px' }}>{jugador.nombre}</strong>
                            {jugador.apodo && (
                              <span style={{ color: '#495057', marginLeft: '8px', fontWeight: '500' }}>
                                ({jugador.apodo})
                              </span>
                            )}
                            {jugador.anoNacimiento && (
                              <span style={{ color: '#6c757d', marginLeft: '6px', fontSize: '12px' }}>
                                · {jugador.anoNacimiento}
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {jugador.esTitular && (
                              <Badge bg="success" style={{ fontSize: '10px' }}>
                                TITULAR
                              </Badge>
                            )}
                            <Badge
                              style={{
                                backgroundColor: getPosicionBorderColor(jugador.posicionAsignada),
                                fontSize: '10px',
                                color: 'white'
                              }}
                            >
                              {(jugador.posicionAsignadaNombre || getPosicionTexto(jugador.posicionAsignada)).toUpperCase()}
                            </Badge>
                          </div>
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#6c757d',
                          marginTop: '4px'
                        }}>
                          <span style={{ color: getPosicionBorderColor(jugador.posicionAsignada), fontWeight: '500' }}>
                            {jugador.posicionAsignadaNombre || getPosicionTexto(jugador.posicionAsignada)}
                          </span>
                          {jugador.posicionPrincipalNombre && (
                            <span style={{ marginLeft: '8px', fontSize: '11px' }}>
                              (Natural: {jugador.posicionPrincipalNombre}{jugador.posicionSecundariaNombre ? ` / ${jugador.posicionSecundariaNombre}` : ''})
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Alert variant="info">
                    No hay jugadores asignados a este equipo
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}

        {/* Equipo Rival */}
        {partido.equipoRival && (
          <Col lg={6} style={{ marginBottom: isMobile ? '20px' : '24px' }}>
            <Card style={{
              border: '2px solid #dc3545',
              borderRadius: '12px'
            }}>
              <Card.Header style={{
                backgroundColor: '#ffebee',
                color: '#1a1a1a',
                fontWeight: '700',
                fontSize: isMobile ? '16px' : '18px',
                border: '2px solid #dc3545',
                borderBottom: '4px solid #dc3545'
              }}>
                ⚔️ Equipo Rival: <span style={{ color: '#dc3545', fontWeight: '800' }}>{partido.equipoRival.nombre}</span>
              </Card.Header>
              <Card.Body style={{ padding: isMobile ? '16px' : '20px' }}>
                {partido.equipoRival.jugadores.length > 0 ? (
                  <div>
                    {ordenarJugadoresPorPosicion(partido.equipoRival.jugadores).map((jugador, _index) => (
                      <div
                        key={jugador.id}
                        style={{
                          padding: '12px',
                          marginBottom: '8px',
                          backgroundColor: getPosicionColor(jugador.posicionAsignada, false),
                          borderRadius: '8px',
                          border: `2px solid ${getPosicionBorderColor(jugador.posicionAsignada)}`,
                          borderLeft: `6px solid ${getPosicionBorderColor(jugador.posicionAsignada)}`,
                          opacity: 0.9
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div>
                            <strong style={{ color: '#1a1a1a', fontSize: '15px' }}>{jugador.nombre}</strong>
                            {jugador.apodo && (
                              <span style={{ color: '#495057', marginLeft: '8px', fontWeight: '500' }}>
                                ({jugador.apodo})
                              </span>
                            )}
                            {jugador.anoNacimiento && (
                              <span style={{ color: '#6c757d', marginLeft: '6px', fontSize: '12px' }}>
                                · {jugador.anoNacimiento}
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {jugador.esTitular && (
                              <Badge bg="danger" style={{ fontSize: '10px' }}>
                                TITULAR
                              </Badge>
                            )}
                            <Badge
                              style={{
                                backgroundColor: getPosicionBorderColor(jugador.posicionAsignada),
                                fontSize: '10px',
                                color: 'white',
                                opacity: 0.8
                              }}
                            >
                              {(jugador.posicionAsignadaNombre || getPosicionTexto(jugador.posicionAsignada)).toUpperCase()}
                            </Badge>
                          </div>
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#6c757d',
                          marginTop: '4px'
                        }}>
                          <span style={{ color: getPosicionBorderColor(jugador.posicionAsignada), fontWeight: '500' }}>
                            {jugador.posicionAsignadaNombre || getPosicionTexto(jugador.posicionAsignada)}
                          </span>
                          {jugador.posicionPrincipalNombre && (
                            <span style={{ marginLeft: '8px', fontSize: '11px' }}>
                              (Natural: {jugador.posicionPrincipalNombre}{jugador.posicionSecundariaNombre ? ` / ${jugador.posicionSecundariaNombre}` : ''})
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Alert variant="info">
                    No hay jugadores asignados a este equipo
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      {/* Acciones */}
      {['confirmado', 'finalizado'].includes(partido.estado) && (
        <Card style={{
          marginTop: isMobile ? '20px' : '24px',
          border: '1px solid #e1e5e9',
          borderRadius: '12px'
        }}>
          <Card.Body style={{
            padding: isMobile ? '20px' : '24px',
            textAlign: 'center'
          }}>
            <h5 style={{ color: '#061156', marginBottom: '16px' }}>
              ⭐ Acciones del Partido
            </h5>
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '12px',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <Button
                variant="primary"
                size="lg"
                style={{
                  backgroundColor: '#4352A9',
                  borderColor: '#4352A9',
                  padding: '12px 24px',
                  width: isMobile ? '100%' : 'auto'
                }}
                onClick={() => navigate(`/coach/partidos/${partido.id}/propoints`)}
              >
                {partido.estado === 'finalizado' ? '📊 Ver ProPoints' : '⭐ Asignar ProPoints'}
              </Button>

              {/* Botón de notificación solo si el partido está finalizado y tiene logros */}
              {(() => {
                console.log('🎯 Renderizando botón - Estado:', partido.estado, 'TieneLogros:', tieneLogros);
                return partido.estado === 'finalizado' && tieneLogros;
              })() && (
                <Button
                  variant="success"
                  size="lg"
                  style={{
                    padding: '12px 24px',
                    width: isMobile ? '100%' : 'auto',
                    backgroundColor: '#28a745',
                    borderColor: '#28a745'
                  }}
                  onClick={handleNotificarProPoints}
                  disabled={enviandoNotificacion}
                >
                  {enviandoNotificacion ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        style={{ marginRight: '8px' }}
                      />
                      Enviando...
                    </>
                  ) : (
                    <>🔔 Notificar Entrega de ProPoints</>
                  )}
                </Button>
              )}
            </div>

            {/* Mensaje informativo si el partido está finalizado pero no tiene logros */}
            {partido.estado === 'finalizado' && !tieneLogros && (
              <Alert variant="info" style={{ marginTop: '16px', marginBottom: 0 }}>
                ℹ️ Asigna ProPoints primero para poder notificar a los tutores y futbolistas
              </Alert>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default CoachPartidoDetalles;
